import { AdapterContext, ValueAdapter as ShellValueAdapter } from 'shell-models-lib';
import { IModel, IAttribute } from 'app/model/base/annotation';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';

/**
 *  When the swagger is not completely handled in Madrid or new attributes are added which
 *  Madrid is not aware of you can use this adapter to capture all the unknown
 *  properties that can be transported back and forth. The flow always applies
 *  the tool supported attributes and then the user entered version.
 *  There are no checks for compatability or validation done with the user
 *  entered JSON and he can potentially corrupt the object
 */
export class ValueAdapter {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static toResource(object: {}, attribute: IAttribute, stringLiteralTemplateValue: boolean = false): any {
        const value = attribute.templateValue(object);
        if (value !== undefined) {
            return stringLiteralTemplateValue
                ? value
                : {
                      value,
                      type: 'Expression'
                  };
        } else {
            return attribute.value(object);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static toModel(object: {}, attribute: IAttribute, value: any): void {
        // null value is user specified, only skip undefined here
        if (value === undefined) {
            return;
        }
        if (value != null && value['type'] === 'Expression') {
            attribute.setTemplateValue(object, value.value);
        } else if (value !== undefined) {
            if (typeof value === 'string' && isLogicAppExpression(value)) {
                attribute.setTemplateValue(object, value);
            } else {
                attribute.setValue(object, value);
            }
        }
    }

    /**
     * Determines whether the given object attribute has a template value in it or just a regular value.
     * @param object The object containing the attribute.
     * @param attribute The attribute to check.
     */
    public static isTemplated(object: {}, attribute: IAttribute): boolean {
        return ShellValueAdapter.isTemplated(object, attribute);
    }

    /**
     * Get plaln-text templated value if exists.
     * @param object The object containing the attribute.
     * @param attribute The attribute to check.
     */
    public static getTemplatedValue(object: {}, attribute: IAttribute): string {
        if (this.isTemplated(object, attribute)) {
            return attribute.templateValue(object);
        }
        return undefined;
    }

    /**
     * Performs a object - spec in spirit(handles arrays and collection more meaningfully)
     * Knows how deep to traverse
     */
    public static remainder(context: AdapterContext, object: {}, model: IModel): Promise<{}> | undefined {
        return ShellValueAdapter.remainder(context, object, model);
    }

    public static remainderWithExternalSpec(context: AdapterContext, object: {}, spec: {}): Promise<{}> {
        return ShellValueAdapter.remainderWithExternalSpec(context, object, spec);
    }
}
